import { z } from 'zod';
import { Id, TimestampMs } from './common.js';
/**
 * G14 / V35 — never stall on usage limits. Work that hits a provider usage limit is parked with
 * its exact resume point and resumed automatically at the reset. The park travels inside the
 * run's (encrypted) transcript as a `notice{code:'usage-limit:*', usageLimit}`; the hub never
 * sees it in plaintext. Additive-only.
 */
/** Where the resume time came from (shown in the drill-down). */
export const UsageLimitResetSource = z.enum([
    /** Provider rate-limit event (Claude `rate_limit_event` rejected window, Codex rate limits). */
    'provider-event',
    /** Parsed from the provider's message ("resets 5am (America/Vancouver)"). */
    'message',
    /** HTTP Retry-After. */
    'retry-after',
    /** Last observed usage window for the account. */
    'window',
    /** No reset stated: conservative backoff with a headroom probe. */
    'fallback',
]);
export const UsageLimitParkState = z.enum([
    'parked',
    'resuming',
    'resumed',
    /** Continued on another account (handoff run). */
    'switched',
    'failed',
    'cancelled',
]);
export const UsageLimitSubjectKind = z.enum(['run', 'landing', 'job']);
/** A subagent that was in flight when the limit hit (Claude Task/Agent, Codex sub-agent). */
export const UsageLimitSubagent = z.object({
    toolCallId: z.string().max(200),
    /** Subagent type + description (the goal), as the agent wrote it. */
    label: z.string().max(400),
    state: z.enum(['interrupted', 'reissued', 'completed', 'abandoned']),
});
/** A Viko-level child (spawned run, landing fix-up) and its park state. */
export const UsageLimitChild = z.object({
    subject: z.string().max(200),
    label: z.string().max(400).optional(),
    state: z.enum(['parked', 'resumed', 'completed', 'running', 'failed']),
});
export const UsageLimitPark = z.object({
    parkId: Id,
    state: UsageLimitParkState,
    subjectKind: UsageLimitSubjectKind,
    provider: z.string().max(40),
    /** Machine-local provider account (never substituted when pinned). */
    accountId: z.string().max(160).optional(),
    /** The user chose this account explicitly: never switched automatically. */
    pinned: z.boolean().optional(),
    /** Provider-stated reset, when known. */
    resetsAt: TimestampMs.optional(),
    /** When Viko will try (reset + small jitter, or the fallback probe time). */
    resumeAt: TimestampMs,
    resetSource: UsageLimitResetSource,
    /** IANA zone the provider stated the reset in, when it did. */
    timeZone: z.string().max(80).optional(),
    parkedAt: TimestampMs,
    attempts: z.number().int().nonnegative(),
    /** Messages held while parked (delivered in order on resume). */
    queued: z.number().int().nonnegative().optional(),
    subagents: z.array(UsageLimitSubagent).max(50).optional(),
    children: z.array(UsageLimitChild).max(50).optional(),
    /** Continued on this account / run after a switch. */
    switchedTo: z.object({ accountId: z.string().max(160), runId: z.string().max(200) }).optional(),
    error: z.string().max(500).optional(),
});
/** Notice codes that carry `usageLimit`. */
export const USAGE_LIMIT_NOTICE = {
    parked: 'usage-limit:parked',
    resumed: 'usage-limit:resumed',
    failed: 'usage-limit:failed',
    switched: 'usage-limit:switched',
    held: 'usage-limit:held',
};
export const USAGE_LIMIT_COMMAND_PAYLOADS = {
    /** Resume a parked item now (skips the wait; still verifies headroom unless `force`). */
    'limit.resume_now': z.object({ parkId: Id, force: z.boolean().optional() }),
    /** Continue a parked item on another account of the same provider (a visible handoff). */
    'limit.switch_account': z.object({ parkId: Id, accountId: Id }),
};
//# sourceMappingURL=usage-limit.js.map